import React, { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { Navigate, useNavigate } from 'react-router-dom';
import StyledComponent from 'styled-components';

import ApiRequest from '../../api/request';
import { ApiManager } from '../../managers';
import { LOGIN_FORM_INPUTS } from './Login.constants';
import { LoginRequest, LoginResponse } from './Login.types';
import { CookieValues } from '@bit-by-bit-software/types';
import { AlertContext, FormLoader, FormValidated, FormValidatedRef } from '@bit-by-bit-software/ui';

const Container = StyledComponent.div`
  margin-top: 100px;
  margin-bottom: 100px;

  form {
    width: 420px;
  }
`;

function Login() {
  const navigate = useNavigate();

  const { showAlert } = useContext(AlertContext);

  const [cookies, setCookies] = useCookies<'token' | 'user', CookieValues>(['token', 'user']);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const formRef = useRef<FormValidatedRef>(null);

  const formOnSubmit = useCallback(
    async (evt: any, buttonData: any) => {
      try {
        evt.preventDefault();

        // Validations
        if (isLoading) {
          return false;
        }

        setIsLoading(true);

        const params: any = {};
        for (let i = 0; i < LOGIN_FORM_INPUTS.length; i++) {
          if (!['button', 'space', 'link', '_form_footer_'].includes(LOGIN_FORM_INPUTS[i].type)) {
            params[LOGIN_FORM_INPUTS[i].id] = formRef.current?.getValueFor(LOGIN_FORM_INPUTS[i].id);
          }
        }

        const response = await ApiRequest.sendRequest<LoginRequest, LoginResponse>({
          method: 'post',
          params,
          path: buttonData?.route || 'user/login',
        });

        if (response.data?.error) {
          showAlert({
            headerText: 'Uh-oh',
            detailText: response.data.error,
            variant: 'danger',
            dismissText: 'okay',
          });
          return false;
        }

        if (response.status === 200) {
          const token = response.data.tokens.accessToken;

          setCookies('user', response.data.user);
          if (token) {
            setCookies('token', token);
            ApiManager.Init(token);
          }
          navigate('/home');
        }
      } catch (err: any) {
        console.log(err);
        showAlert({
          headerText: 'Uh-oh',
          detailText: `An error has occurred, please try again or contact support.\nError: ${err.emssage}`,
          variant: 'danger',
          dismissText: 'okay',
          err,
        });
      } finally {
        setIsLoading(false);
      }
    },
    [showAlert, navigate, setCookies, isLoading],
  );

  const formInputs = useMemo(
    () =>
      LOGIN_FORM_INPUTS.map(input =>
        input.type === 'button'
          ? {
              ...input,
              onClick: formOnSubmit,
            }
          : input,
      ),
    [formOnSubmit],
  );

  if (cookies.token && cookies.user?.authorization) {
    return <Navigate to="/home" replace />;
  }

  return (
    <Container>
      <FormLoader isLoading={isLoading} />
      <FormValidated
        ref={formRef}
        formClassName="form-width"
        // @ts-ignore
        formInputs={formInputs}
        formOnChange={() => {}}
        validateOnInit={false}
        columns={-1}
        dynamicInputs
        key="login-form"
        description=""
      />
    </Container>
  );
}

export default Login;
